import { cn } from '@neo/lib/utils'
import type { ReactNode, RefObject } from 'react'
import { StepAgent } from './step-agent'
import { StepFramework } from './step-framework'
import { buildOptions, needsFrameworkStep, type StepProps } from './wizard-data'
import { BuildCard } from './wizard-primitives'

// Two of the paths expand a follow-up inline under the selected option, inside
// its border and above a grey panel, with no divider: the bring-your-own-stack
// options (SIP Trunks, Audio Streaming) ask which framework is being connected
// (StepFramework), and the no-code path asks what the agent should do
// (StepAgent, free text, optional — qualification data, never a gate).
//
// The no-code path is set apart by its badge alone (see BUILD_OPTIONS), not by a
// louder card: a resting style stronger than the SELECTED style would invert the
// hierarchy.
export const StepBuild = ({
  state,
  update,
  stepLabel,
  headingRef,
}: StepProps & { stepLabel?: ReactNode; headingRef?: RefObject<HTMLHeadingElement> }) => (
  <div className="flex flex-col gap-4">
    {/* h1, not p: /onboarding otherwise has no heading at all, which leaves a
        screen reader's heading navigation empty. The token carries the size. */}
    <div className="flex items-center justify-between gap-3">
      <h1 ref={headingRef} tabIndex={-1} className="text-h3-500 text-foreground outline-none">
        What do you want to start with?
      </h1>
      {stepLabel}
    </div>
    <div className="flex flex-col gap-3">
      {buildOptions().map((option) => {
        const selected = state.build === option.value
        return (
          <div
            key={option.value}
            // The card stays WHITE when selected — the near-black border and the
            // filled radio carry the selection. The grey belongs to the
            // follow-up panel inside it (StepFramework / StepAgent), which is
            // the repo's existing shape for a nested question: bg-bg3 panel,
            // white controls on top (unified-form.config.tsx does the same for
            // Answer URL). Tinting the whole block was tried first and made the
            // card compete with its own nested question for emphasis.
            className={cn(
              'overflow-hidden rounded-lg border bg-background transition-colors',
              selected ? 'border-primary' : 'border-st2 hover:border-st1',
            )}
          >
            <BuildCard
              option={option}
              selected={selected}
              expanded={
                selected && (needsFrameworkStep(option.value) || option.value === 'voice-ai')
              }
              // Clicking the SELECTED option clears it: these are
              // buttons wearing a radio glyph, not a native radio group, so
              // unpicking is available here in a way it is not on the framework
              // pills — and with the follow-up panels nested inside the selected
              // card, clicking it again is the obvious way to close one you opened
              // to look at. Clearing the build also collapses that panel and
              // re-disables Get started, since isComplete needs a build.
              //
              // Downstream answers reset only on a CHANGE of build — a framework
              // named for SIP means nothing once the build is Audio. Deselecting
              // keeps them: isComplete already fails without a build and
              // freeTextAnswer keys off state.build, so stale text is unreachable
              // while nothing is selected — and a misclick on the selected card
              // must not destroy a half-typed answer.
              onSelect={(v) =>
                update(
                  selected
                    ? { build: '' }
                    : { build: v, framework: '', frameworkDetail: '', agentDescription: '' },
                )
              }
            />
            {selected && needsFrameworkStep(option.value) && (
              <StepFramework state={state} update={update} />
            )}
            {selected && option.value === 'voice-ai' && <StepAgent state={state} update={update} />}
          </div>
        )
      })}
    </div>
  </div>
)
